import {
  ActionTooltip,
  Button,
  Check,
  CircleCheck,
  CircleX,
  Flame,
  HandCoins,
  LoaderCircle,
  ResponsiveDialog,
  WalletCards,
} from "@tapeout/ui";
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { formatUnits, getAddress, isAddress, type Address, type Hex } from "viem";

import { createBatchPlan, type BatchPlan } from "./lib/batchPlan.js";
import type {
  BatchKind,
  MintQuantityMode,
  ProjectSnapshot,
  RuntimeConfig,
  TapeoutMode,
  TapeoutSecret,
  WalletRow,
  WalletStage,
} from "./lib/batchTypes.js";
import {
  createRuntime,
  inspectNandOwnership,
  inspectProject,
  inspectWalletAssets,
  mintNands,
  runTapeout,
  sanitizeChainError,
  type StageUpdate,
} from "./lib/chain.js";
import { EphemeralKeyVault, type ImportIssue } from "./lib/keyVault.js";

const MAX_FILE_BYTES = 256 * 1024;
const WALLET_PAGE_SIZE = 100;
const FIXED_RPC_URL = import.meta.env.VITE_RPC_HTTP_URL?.trim() ?? "";
const FIXED_CHAIN_ID = Number(import.meta.env.VITE_CHAIN_ID ?? "56");
const FIXED_NATIVE_SYMBOL = (import.meta.env.VITE_NATIVE_SYMBOL ?? "BNB").trim().toUpperCase();
const FIXED_SCAN_FROM_BLOCK = BigInt(
  import.meta.env.VITE_SCAN_FROM_BLOCK?.trim() || (FIXED_CHAIN_ID === 56 ? "121646017" : "0"),
);

const stageLabels: Record<WalletStage, string> = {
  idle: "待执行",
  checking: "校验中",
  approving: "授权中",
  minting: "Mint 中",
  committing: "Commit 中",
  waiting: "等待新区块",
  revealing: "Reveal 中",
  success: "已完成",
  skipped: "已跳过",
  failed: "失败",
  uncertain: "结果待核对",
  stopped: "已停止",
};

const stateLabels = ["募资中", "等待流动性", "运行中", "已失败"] as const;

function shortAddress(address: Address): string {
  return `${address.slice(0, 8)}…${address.slice(-6)}`;
}

function shortHash(hash: Hex): string {
  return `${hash.slice(0, 10)}…${hash.slice(-8)}`;
}

function formatAmount(value: bigint, decimals: number): string {
  const formatted = formatUnits(value, decimals);
  const [integer, fraction = ""] = formatted.split(".");
  return fraction === ""
    ? (integer ?? "0")
    : `${integer ?? "0"}.${fraction.slice(0, 6).replace(/0+$/, "") || "0"}`;
}

function initialRow(address: Address, nandIds: readonly bigint[] = []): WalletRow {
  return {
    address,
    selected: true,
    nandIds,
    stage: "idle",
    detail: "待执行",
    txHashes: [],
    minerIds: [],
    completed: 0,
    total: 0,
  };
}

function StageGlyph({ stage }: { readonly stage: WalletStage }) {
  if (["checking", "approving", "minting", "committing", "waiting", "revealing"].includes(stage)) {
    return <LoaderCircle aria-hidden="true" className="spin" size={16} />;
  }
  if (stage === "success") return <CircleCheck aria-hidden="true" size={16} />;
  if (stage === "failed" || stage === "uncertain") return <CircleX aria-hidden="true" size={16} />;
  return <span aria-hidden="true" className="status-dot" />;
}

function Brand() {
  return (
    <div className="brand" aria-label="TAPEOUT V3 批量操作台">
      <img src="/favicon.svg" alt="" />
      <span className="brand-word">TAPEOUT</span>
      <span className="brand-version">V3</span>
      <span className="brand-divider" aria-hidden="true" />
      <span className="brand-tool">BATCH OPERATOR</span>
    </div>
  );
}

function Rail({
  projectReady,
  walletCount,
  running,
}: {
  readonly projectReady: boolean;
  readonly walletCount: number;
  readonly running: boolean;
}) {
  const items = [
    { label: "项目连接", ready: projectReady },
    { label: "临时钱包", ready: walletCount > 0 },
    { label: "独立任务", ready: projectReady && walletCount > 0 },
  ];
  return (
    <ol className="rail" aria-label="批量操作准备进度">
      {items.map((item, index) => (
        <li key={item.label} className={item.ready ? "ready" : ""}>
          <span className="rail-node">
            {item.ready ? <Check aria-hidden="true" size={16} /> : String(index + 1)}
          </span>
          <span>{item.label}</span>
          {index < items.length - 1 ? <span className="rail-line" aria-hidden="true" /> : null}
        </li>
      ))}
      {running ? <li className="rail-running">正在执行链上任务</li> : null}
    </ol>
  );
}

export function App() {
  const vaultRef = useRef(new EphemeralKeyVault());
  const secretsRef = useRef(new Map<string, TapeoutSecret>());
  const stopRef = useRef(false);
  const runningRef = useRef(false);
  const requestSequence = useRef(0);
  const assetRequestSequence = useRef(0);
  const nandRequestSequence = useRef(0);
  const nandOwnershipRef = useRef<ReadonlyMap<string, readonly bigint[]>>(new Map());
  const headerCheckbox = useRef<HTMLInputElement>(null);
  const [projectAddress, setProjectAddress] = useState("");
  const [snapshot, setSnapshot] = useState<ProjectSnapshot | null>(null);
  const [projectBusy, setProjectBusy] = useState(false);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [keyDraft, setKeyDraft] = useState("");
  const [importIssues, setImportIssues] = useState<readonly ImportIssue[]>([]);
  const [fileNotice, setFileNotice] = useState<string | null>(null);
  const [rows, setRows] = useState<readonly WalletRow[]>([]);
  const [mintQuantityMode, setMintQuantityMode] = useState<MintQuantityMode>("fixed");
  const [mintQuantity, setMintQuantity] = useState("5");
  const [mintRandomMin, setMintRandomMin] = useState("5");
  const [mintRandomMax, setMintRandomMax] = useState("50");
  const [tapeoutMode, setTapeoutMode] = useState<TapeoutMode>("count");
  const [tapeoutCount, setTapeoutCount] = useState("1");
  const [assetRefreshNonce, setAssetRefreshNonce] = useState(0);
  const [nandScanStatus, setNandScanStatus] = useState<"idle" | "loading" | "ready" | "failed">(
    "idle",
  );
  const [walletPage, setWalletPage] = useState(0);
  const [planError, setPlanError] = useState<string | null>(null);
  const [pendingPlan, setPendingPlan] = useState<BatchPlan | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [running, setRunning] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [progress, setProgress] = useState<{
    readonly kind: BatchKind;
    readonly done: number;
    readonly total: number;
  } | null>(null);
  const [clearOpen, setClearOpen] = useState(false);

  const selectedCount = rows.filter((row) => row.selected).length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const walletPageCount = Math.max(1, Math.ceil(rows.length / WALLET_PAGE_SIZE));
  const visibleRows = rows.slice(
    walletPage * WALLET_PAGE_SIZE,
    (walletPage + 1) * WALLET_PAGE_SIZE,
  );
  const visibleAddressKey = visibleRows.map((row) => row.address.toLowerCase()).join("|");

  useEffect(() => {
    document.title = "批量操作台 | TAPEOUT V3";
  }, []);

  useEffect(() => {
    if (headerCheckbox.current !== null) headerCheckbox.current.indeterminate = someSelected;
  }, [someSelected]);

  useEffect(() => {
    runningRef.current = running;
  }, [running]);

  useEffect(() => {
    setWalletPage((current) => Math.min(current, walletPageCount - 1));
  }, [walletPageCount]);

  useEffect(() => {
    if (snapshot === null || visibleAddressKey === "" || running) return;
    const sequence = ++assetRequestSequence.current;
    const addresses = visibleRows.map((row) => row.address);
    const runtime = createRuntime({
      rpcUrl: FIXED_RPC_URL,
      chainId: FIXED_CHAIN_ID,
      nativeSymbol: FIXED_NATIVE_SYMBOL,
      projectAddress: snapshot.address,
      scanFromBlock: FIXED_SCAN_FROM_BLOCK,
    });
    void inspectWalletAssets(runtime, snapshot, addresses, (asset) => {
      if (assetRequestSequence.current !== sequence) return;
      setRows((current) =>
        current.map((row) =>
          row.address.toLowerCase() === asset.address.toLowerCase()
            ? {
                ...row,
                paymentBalance: asset.paymentBalance,
                nandBalance: asset.nandBalance,
                minerBalance: asset.minerBalance,
                assetError: undefined,
              }
            : row,
        ),
      );
    })
      .then(() => {
        if (assetRequestSequence.current !== sequence) return;
      })
      .catch((reason: unknown) => {
        if (assetRequestSequence.current !== sequence) return;
        const message = sanitizeChainError(reason);
        const targets = new Set(addresses.map((address) => address.toLowerCase()));
        setRows((current) =>
          current.map((row) =>
            targets.has(row.address.toLowerCase()) ? { ...row, assetError: message } : row,
          ),
        );
      });
    return () => {
      if (assetRequestSequence.current === sequence) assetRequestSequence.current += 1;
    };
  }, [assetRefreshNonce, running, snapshot, visibleAddressKey]);

  useEffect(() => {
    if (snapshot === null) {
      nandOwnershipRef.current = new Map();
      setNandScanStatus("idle");
      return;
    }
    const sequence = ++nandRequestSequence.current;
    setNandScanStatus("loading");
    const runtime = createRuntime({
      rpcUrl: FIXED_RPC_URL,
      chainId: FIXED_CHAIN_ID,
      nativeSymbol: FIXED_NATIVE_SYMBOL,
      projectAddress: snapshot.address,
      scanFromBlock: FIXED_SCAN_FROM_BLOCK,
    });
    void inspectNandOwnership(runtime, snapshot)
      .then((ownership) => {
        if (nandRequestSequence.current !== sequence) return;
        nandOwnershipRef.current = ownership;
        setRows((current) =>
          current.map((row) => ({
            ...row,
            nandIds: ownership.get(row.address.toLowerCase()) ?? [],
          })),
        );
        setNandScanStatus("ready");
      })
      .catch(() => {
        if (nandRequestSequence.current === sequence) setNandScanStatus("failed");
      });
    return () => {
      if (nandRequestSequence.current === sequence) nandRequestSequence.current += 1;
    };
  }, [assetRefreshNonce, snapshot]);

  useEffect(() => {
    const clearSecrets = () => vaultRef.current.clear();
    const warnBeforeLeave = (event: BeforeUnloadEvent) => {
      if (!runningRef.current && secretsRef.current.size === 0) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("pagehide", clearSecrets);
    window.addEventListener("beforeunload", warnBeforeLeave);
    return () => {
      window.removeEventListener("pagehide", clearSecrets);
      window.removeEventListener("beforeunload", warnBeforeLeave);
      vaultRef.current.clear();
    };
  }, []);

  function invalidateProject(): void {
    requestSequence.current += 1;
    setSnapshot(null);
    setProjectError(null);
    setProjectBusy(false);
  }

  function runtimeConfig(): RuntimeConfig {
    if (FIXED_RPC_URL === "") throw new Error("构建环境未配置固定 RPC 地址");
    if (!Number.isSafeInteger(FIXED_CHAIN_ID) || FIXED_CHAIN_ID <= 0) {
      throw new Error("构建环境的链 ID 无效");
    }
    if (!isAddress(projectAddress)) throw new Error("请输入有效的 ProjectCore 地址");
    if (!/^[A-Z0-9]{2,8}$/.test(FIXED_NATIVE_SYMBOL)) {
      throw new Error("构建环境的原生代币符号无效");
    }
    return {
      rpcUrl: FIXED_RPC_URL,
      chainId: FIXED_CHAIN_ID,
      nativeSymbol: FIXED_NATIVE_SYMBOL,
      projectAddress: getAddress(projectAddress),
      scanFromBlock: FIXED_SCAN_FROM_BLOCK,
    };
  }

  async function checkProject(): Promise<void> {
    const sequence = ++requestSequence.current;
    setProjectBusy(true);
    setProjectError(null);
    try {
      const result = await inspectProject(runtimeConfig());
      if (requestSequence.current === sequence) setSnapshot(result);
    } catch (reason) {
      if (requestSequence.current === sequence) {
        setSnapshot(null);
        setProjectError(sanitizeChainError(reason));
      }
    } finally {
      if (requestSequence.current === sequence) setProjectBusy(false);
    }
  }

  function mergeImport(raw: string, sourceLabel: string): void {
    const result = vaultRef.current.importText(raw);
    setImportIssues(result.issues);
    setKeyDraft("");
    setRows((current) => {
      const next = [...current];
      for (const wallet of result.wallets) {
        const index = next.findIndex(
          (row) => row.address.toLowerCase() === wallet.address.toLowerCase(),
        );
        if (index === -1) {
          next.push(
            initialRow(
              wallet.address,
              nandOwnershipRef.current.get(wallet.address.toLowerCase()) ?? [],
            ),
          );
        }
      }
      return next;
    });
    setFileNotice(
      result.wallets.length > 0
        ? `${sourceLabel}：已读取 ${result.wallets.length} 行，密钥输入已清空`
        : `${sourceLabel}：没有导入钱包`,
    );
  }

  async function chooseFile(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file === undefined) return;
    if (file.size > MAX_FILE_BYTES) {
      setFileNotice("文件超过 256 KB，未读取");
      return;
    }
    try {
      mergeImport(await file.text(), `${file.name}（${Math.ceil(file.size / 1024)} KB）`);
    } catch {
      setFileNotice("无法读取本地文件，请使用 UTF-8 TXT 或 CSV");
    }
  }

  function updateRow(address: Address, patch: Partial<WalletRow>): void {
    setRows((current) =>
      current.map((row) =>
        row.address.toLowerCase() === address.toLowerCase() ? { ...row, ...patch } : row,
      ),
    );
  }

  function removeRow(address: Address): void {
    if (running) return;
    setRows((current) =>
      current.filter((row) => row.address.toLowerCase() !== address.toLowerCase()),
    );
  }

  function preparePlan(kind: BatchKind): void {
    setPlanError(null);
    if (snapshot === null) {
      setPlanError("请先校验项目");
      return;
    }
    try {
      const plan = createBatchPlan({
        kind,
        rows,
        snapshot,
        mintQuantityMode,
        mintQuantity: BigInt(mintQuantity),
        mintRandomMin: BigInt(mintRandomMin),
        mintRandomMax: BigInt(mintRandomMax),
        tapeoutMode,
        tapeoutCount: Number(tapeoutCount),
      });
      setAcknowledged(false);
      setPendingPlan(plan);
    } catch (reason) {
      setPlanError(sanitizeChainError(reason));
    }
  }

  function applyStage(address: Address, update: StageUpdate, prefix = ""): void {
    updateRow(address, {
      stage: update.stage,
      detail: prefix === "" ? update.detail : `${prefix} · ${update.detail}`,
      ...(update.hash === undefined ? {} : { txHash: update.hash }),
    });
  }

  async function executePlan(): Promise<void> {
    if (pendingPlan === null || snapshot === null || !acknowledged || running) return;
    const plan = pendingPlan;
    const currentSnapshot = snapshot;
    const plannedRows = rows;
    setPendingPlan(null);
    setRunning(true);
    setStopping(false);
    stopRef.current = false;
    const totalWallets = plan.addresses.length + plan.skippedAddresses.length;
    setProgress({ kind: plan.kind, done: plan.skippedAddresses.length, total: totalWallets });
    for (const address of plan.skippedAddresses) {
      updateRow(address, {
        stage: "skipped",
        detail: "可用 NAND 不足 5 枚，已跳过",
        completed: 0,
        total: 0,
      });
    }
    for (const address of plan.addresses) {
      const taskTotal =
        plan.kind === "mint"
          ? (plan.allocations.find((item) => item.address.toLowerCase() === address.toLowerCase())
              ?.chunks.length ?? 0)
          : (plan.allocations.find((item) => item.address.toLowerCase() === address.toLowerCase())
              ?.groups.length ?? 0);
      updateRow(address, {
        stage: "idle",
        detail: "等待执行",
        completed: 0,
        total: taskTotal,
        txHashes: [],
        minerIds:
          plan.kind === "tapeout"
            ? []
            : (rows.find((row) => row.address === address)?.minerIds ?? []),
      });
    }

    try {
      const runtime = createRuntime(runtimeConfig());
      for (const [walletIndex, address] of plan.addresses.entries()) {
        if (stopRef.current) {
          for (const remaining of plan.addresses.slice(walletIndex)) {
            updateRow(remaining, { stage: "stopped", detail: "未广播交易" });
          }
          break;
        }
        const account = vaultRef.current.accountFor(address);
        if (account === undefined) {
          updateRow(address, { stage: "failed", detail: "会话密钥已清除，请重新导入" });
          setProgress({
            kind: plan.kind,
            done: plan.skippedAddresses.length + walletIndex + 1,
            total: totalWallets,
          });
          continue;
        }
        let lastUpdate: StageUpdate = { stage: "idle", detail: "等待执行" };
        try {
          if (plan.kind === "mint") {
            const allocation = plan.allocations.find(
              (item) => item.address.toLowerCase() === address.toLowerCase(),
            );
            if (allocation === undefined) throw new Error("钱包 Mint 计划不存在");
            const existing =
              plannedRows.find((row) => row.address.toLowerCase() === address.toLowerCase())
                ?.nandIds ?? [];
            const mintedIds: bigint[] = [];
            const txHashes: Hex[] = [];
            let approvalHash: Hex | undefined;
            for (const [chunkIndex, quantity] of allocation.chunks.entries()) {
              if (stopRef.current && chunkIndex > 0) {
                updateRow(address, {
                  stage: "stopped",
                  detail: `已完成 ${chunkIndex}/${allocation.chunks.length} 笔，后续未广播`,
                  completed: chunkIndex,
                  txHashes,
                });
                break;
              }
              const result = await mintNands({
                runtime,
                snapshot: currentSnapshot,
                account,
                quantity,
                ...(chunkIndex === 0
                  ? { approvalAmount: currentSnapshot.nandPrice * allocation.quantity }
                  : {}),
                onUpdate: (update) => {
                  lastUpdate = update;
                  applyStage(
                    address,
                    update,
                    `第 ${chunkIndex + 1}/${allocation.chunks.length} 笔`,
                  );
                },
              });
              mintedIds.push(...result.ids);
              txHashes.push(result.hash);
              approvalHash ??= result.approvalHash;
              updateRow(address, {
                stage: chunkIndex + 1 === allocation.chunks.length ? "success" : "checking",
                detail: `已完成 ${chunkIndex + 1}/${allocation.chunks.length} 笔 Mint`,
                txHash: result.hash,
                txHashes: [...txHashes],
                ...(approvalHash === undefined ? {} : { approvalHash }),
                completed: chunkIndex + 1,
                total: allocation.chunks.length,
              });
            }
            if (mintedIds.length === 0) continue;
            const ids = [...new Set([...existing, ...mintedIds])];
            updateRow(address, {
              stage: mintedIds.length === Number(allocation.quantity) ? "success" : "stopped",
              detail: `Mint ${mintedIds.length}/${allocation.quantity.toString()} 枚：NAND #${mintedIds[0]?.toString() ?? "?"}–#${mintedIds.at(-1)?.toString() ?? "?"}`,
              nandIds: ids,
              completed: txHashes.length,
              total: allocation.chunks.length,
            });
          } else {
            const allocation = plan.allocations.find(
              (item) => item.address.toLowerCase() === address.toLowerCase(),
            );
            if (allocation === undefined) throw new Error("钱包流片计划不存在");
            const minerIds: bigint[] = [];
            const txHashes: Hex[] = [];
            for (const [runIndex, group] of allocation.groups.entries()) {
              if (stopRef.current) {
                updateRow(address, {
                  stage: "stopped",
                  detail: `已完成 ${runIndex}/${allocation.groups.length} 次，后续未广播`,
                  completed: runIndex,
                  minerIds,
                  txHashes,
                });
                break;
              }
              const secretKey = `${address.toLowerCase()}:${runIndex}`;
              const result = await runTapeout({
                runtime,
                snapshot: currentSnapshot,
                account,
                nandIds: group,
                ...(secretsRef.current.has(secretKey)
                  ? { existingSecret: secretsRef.current.get(secretKey) as TapeoutSecret }
                  : {}),
                onSecret: (secret) => secretsRef.current.set(secretKey, secret),
                onUpdate: (update) => {
                  lastUpdate = update;
                  applyStage(address, update, `第 ${runIndex + 1}/${allocation.groups.length} 次`);
                },
              });
              secretsRef.current.delete(secretKey);
              minerIds.push(result.minerId);
              txHashes.push(result.commitHash, result.revealHash);
              updateRow(address, {
                stage: runIndex + 1 === allocation.groups.length ? "success" : "checking",
                detail: `已完成 ${runIndex + 1}/${allocation.groups.length} 次流片`,
                txHash: result.revealHash,
                txHashes: [...txHashes],
                commitHash: result.commitHash,
                minerIds: [...minerIds],
                completed: runIndex + 1,
                total: allocation.groups.length,
              });
            }
          }
        } catch (reason) {
          const uncertain =
            lastUpdate.hash !== undefined &&
            (lastUpdate.stage === "committing" ||
              lastUpdate.stage === "revealing" ||
              lastUpdate.stage === "minting" ||
              lastUpdate.stage === "approving");
          updateRow(address, {
            stage: uncertain ? "uncertain" : "failed",
            detail: sanitizeChainError(reason),
            ...(lastUpdate.hash === undefined ? {} : { txHash: lastUpdate.hash }),
          });
        }
        setProgress({
          kind: plan.kind,
          done: plan.skippedAddresses.length + walletIndex + 1,
          total: totalWallets,
        });
      }
      try {
        setSnapshot(await inspectProject(runtimeConfig()));
        setAssetRefreshNonce((current) => current + 1);
      } catch {
        // Results remain usable even if the final non-mutating refresh fails.
      }
    } finally {
      setRunning(false);
      setStopping(false);
      stopRef.current = false;
    }
  }

  function stopFutureTasks(): void {
    stopRef.current = true;
    setStopping(true);
  }

  function clearSession(): void {
    if (running) return;
    vaultRef.current.clear();
    secretsRef.current.clear();
    setRows([]);
    setKeyDraft("");
    setImportIssues([]);
    setFileNotice("钱包列表已清空，内存中的签名账户已移除");
    setProgress(null);
    setWalletPage(0);
    setClearOpen(false);
  }

  function exportReport(): void {
    const report = {
      exportedAt: new Date().toISOString(),
      chainId: snapshot?.actualChainId ?? null,
      project: snapshot?.address ?? null,
      wallets: rows.map((row) => ({
        address: row.address,
        stage: row.stage,
        detail: row.detail,
        nandIds: row.nandIds.map(String),
        minerIds: row.minerIds.map(String),
        txHash: row.txHash ?? null,
        txHashes: row.txHashes,
        approvalHash: row.approvalHash ?? null,
        commitHash: row.commitHash ?? null,
      })),
      containsPrivateKeys: false,
    };
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(report, null, 2)], { type: "application/json" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `tapeout-batch-${Date.now()}.json`;
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
  }

  const actionDisabledReason = running
    ? "已有批次正在执行"
    : snapshot === null
      ? "请先校验项目连接"
      : rows.length === 0
        ? "请先导入钱包"
        : selectedCount === 0
          ? "请至少选择一个钱包"
          : null;
  const tapeoutDisabledReason =
    actionDisabledReason ??
    (nandScanStatus === "loading"
      ? "正在同步链上 NAND ID"
      : nandScanStatus === "failed"
        ? "NAND ID 同步失败，请重新校验项目"
        : null);

  return (
    <div className="app-shell">
      <header className="app-header">
        <Brand />
        <div className="header-meta">
          <span className="local-pill">
            <span aria-hidden="true" />
            本地内存模式
          </span>
          <span>{rows.length} 个钱包</span>
        </div>
      </header>

      <main>
        <section className="hero" aria-labelledby="page-title">
          <div>
            <p className="eyebrow">WAFER LANE / LOCAL SIGNING</p>
            <h1 id="page-title">批量链上操作台</h1>
            <p className="hero-copy">
              先校验项目，再导入钱包。Mint NAND 与流片是两个独立任务，均由你单独确认和启动。
            </p>
          </div>
          <Rail projectReady={snapshot !== null} walletCount={rows.length} running={running} />
        </section>

        <div className="security-banner" role="note">
          <span className="security-mark" aria-hidden="true">
            !
          </span>
          <div>
            <strong>私钥只保留在本机</strong>
            <p>
              私钥原文仅在当前页面的本机内存中用于签名，不会随 API 或 RPC 请求发送，也不会写入
              localStorage /
              IndexedDB、日志或导出文件。网络只接收公开地址、链上查询和本机签名后的交易数据；离开页面即清空。
            </p>
          </div>
        </div>

        <section className="workspace-grid">
          <article className="panel" aria-labelledby="project-heading">
            <div className="panel-heading">
              <span className="step-index">01</span>
              <div>
                <h2 id="project-heading">连接目标项目</h2>
                <p>一次批次只作用于一个 ProjectCore。</p>
              </div>
            </div>
            <form
              className="connection-form"
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                void checkProject();
              }}
            >
              <div className="fixed-network field-full" aria-label="固定网络配置">
                <span className="status-dot" aria-hidden="true" />
                <strong>{FIXED_CHAIN_ID === 56 ? "BSC 主网" : `Chain ${FIXED_CHAIN_ID}`}</strong>
                <span>Chain {FIXED_CHAIN_ID}</span>
                <span>{FIXED_NATIVE_SYMBOL}</span>
                <span className="fixed-network-note">RPC 已由构建配置固定</span>
              </div>
              <label className="field field-full">
                <span>ProjectCore 地址</span>
                <input
                  className="mono"
                  value={projectAddress}
                  onChange={(event) => {
                    setProjectAddress(event.target.value);
                    invalidateProject();
                  }}
                  placeholder="0x…"
                  spellCheck={false}
                  aria-invalid={projectError !== null}
                  aria-describedby="project-help project-error"
                />
              </label>
              <p id="project-help" className="field-help field-full">
                工具会直接从链上读取状态、价格、支付资产和 NAND 合约。
              </p>
              {projectError === null ? null : (
                <p id="project-error" className="form-error field-full" role="alert">
                  {projectError}
                </p>
              )}
              <Button type="submit" disabled={projectBusy} className="field-full action-fit">
                {projectBusy ? <LoaderCircle className="spin" aria-hidden="true" /> : null}
                {projectBusy ? "正在校验" : "校验项目"}
              </Button>
            </form>
            {snapshot === null ? null : (
              <div className="project-result" role="status">
                <div className="project-token">
                  <CircleCheck aria-hidden="true" />
                  <div>
                    <strong>{snapshot.tokenName}</strong>
                    <span>
                      {snapshot.tokenSymbol} · Chain {snapshot.actualChainId}
                    </span>
                  </div>
                </div>
                <dl>
                  <div>
                    <dt>状态</dt>
                    <dd>{stateLabels[snapshot.state] ?? `状态 ${snapshot.state}`}</dd>
                  </div>
                  <div>
                    <dt>NAND</dt>
                    <dd className="mono">
                      {snapshot.sold.toString()} / {snapshot.nandCap.toString()}
                    </dd>
                  </div>
                  <div>
                    <dt>单价</dt>
                    <dd className="mono">
                      {formatAmount(snapshot.nandPrice, snapshot.paymentDecimals)}{" "}
                      {snapshot.paymentSymbol}
                    </dd>
                  </div>
                  <div>
                    <dt>募集资产</dt>
                    <dd title={snapshot.paymentKind === 1 ? snapshot.paymentToken : "原生代币"}>
                      {snapshot.paymentSymbol} ·{" "}
                      {snapshot.paymentKind === 1 ? "ERC‑20" : "原生代币"}
                    </dd>
                  </div>
                </dl>
              </div>
            )}
          </article>

          <article className="panel" aria-labelledby="wallet-heading">
            <div className="panel-heading">
              <span className="step-index">02</span>
              <div>
                <h2 id="wallet-heading">导入临时钱包</h2>
                <p>每行一个私钥；钱包资产与 NAND ID 会从目标项目自动读取。</p>
              </div>
            </div>
            <form
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                mergeImport(keyDraft, "粘贴内容");
              }}
            >
              <label className="field secret-field">
                <span>钱包私钥</span>
                <textarea
                  value={keyDraft}
                  onChange={(event) => setKeyDraft(event.target.value)}
                  className="mono resize-none secret-masked"
                  placeholder="0x私钥&#10;0x私钥"
                  spellCheck={false}
                  autoComplete="off"
                  aria-describedby="secret-help import-status import-errors"
                />
              </label>
              <p id="secret-help" className="field-help">
                TXT / CSV 每行仅一个私钥，最多导入 2,000
                个钱包；文件只在本机读取，不会上传，输入在导入后立即清空。
              </p>
              <div className="import-actions">
                <label className="file-button">
                  选择本地文件
                  <input
                    type="file"
                    accept=".txt,.csv,text/plain,text/csv"
                    onChange={(event) => {
                      void chooseFile(event);
                    }}
                  />
                </label>
                <Button type="submit" disabled={keyDraft.trim() === ""}>
                  导入并清空输入
                </Button>
              </div>
            </form>
            <div id="import-status" className="import-status" role="status">
              {fileNotice ?? "尚未读取本地文件"}
            </div>
            {importIssues.length === 0 ? null : (
              <div id="import-errors" className="issue-list" role="alert">
                <strong>{importIssues.length} 行未导入</strong>
                <ul>
                  {importIssues.slice(0, 6).map((issue) => (
                    <li key={`${issue.line}-${issue.reason}`}>
                      第 {issue.line} 行：{issue.reason}
                    </li>
                  ))}
                </ul>
                {importIssues.length > 6 ? <p>另有 {importIssues.length - 6} 条错误</p> : null}
              </div>
            )}
          </article>
        </section>

        <section className="action-section" aria-labelledby="actions-heading">
          <div className="section-title-row">
            <div>
              <p className="eyebrow">03 / INDEPENDENT JOBS</p>
              <h2 id="actions-heading">选择独立任务</h2>
            </div>
          </div>
          {planError === null ? null : (
            <div className="form-error action-error" role="alert">
              {planError}
            </div>
          )}
          <div className="action-cards">
            <article className="operation-card">
              <div className="operation-icon mint">
                <HandCoins aria-hidden="true" />
              </div>
              <div className="operation-copy">
                <h3>批量 Mint NAND</h3>
                <p>固定或随机分配每钱包数量；单钱包超过 50 枚时自动拆单。</p>
              </div>
              <div className="operation-controls">
                <fieldset className="mode-switch">
                  <legend>数量模式</legend>
                  <label>
                    <input
                      type="radio"
                      name="mint-quantity-mode"
                      value="fixed"
                      checked={mintQuantityMode === "fixed"}
                      onChange={() => setMintQuantityMode("fixed")}
                      disabled={running}
                    />
                    <span>固定数量</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="mint-quantity-mode"
                      value="random"
                      checked={mintQuantityMode === "random"}
                      onChange={() => setMintQuantityMode("random")}
                      disabled={running}
                    />
                    <span>随机数量</span>
                  </label>
                </fieldset>
                {mintQuantityMode === "fixed" ? (
                  <label className="compact-field">
                    <span>每钱包数量</span>
                    <input
                      type="number"
                      min="5"
                      max="195"
                      step="5"
                      value={mintQuantity}
                      onChange={(event) => setMintQuantity(event.target.value)}
                      disabled={running}
                    />
                  </label>
                ) : (
                  <div className="range-fields">
                    <label className="compact-field">
                      <span>随机下限</span>
                      <input
                        type="number"
                        min="5"
                        max="195"
                        step="5"
                        value={mintRandomMin}
                        onChange={(event) => setMintRandomMin(event.target.value)}
                        disabled={running}
                      />
                    </label>
                    <label className="compact-field">
                      <span>随机上限</span>
                      <input
                        type="number"
                        min="5"
                        max="195"
                        step="5"
                        value={mintRandomMax}
                        onChange={(event) => setMintRandomMax(event.target.value)}
                        disabled={running}
                      />
                    </label>
                  </div>
                )}
                <p className="control-help">5–195，必须为 5 的倍数。</p>
              </div>
              <ActionTooltip disabled={actionDisabledReason !== null} label={actionDisabledReason}>
                <Button
                  disabled={actionDisabledReason !== null}
                  onClick={() => preparePlan("mint")}
                >
                  检查并确认 Mint
                </Button>
              </ActionTooltip>
            </article>
            <article className="operation-card">
              <div className="operation-icon tapeout">
                <Flame aria-hidden="true" />
              </div>
              <div className="operation-copy">
                <h3>批量流片</h3>
                <p>每次消耗 5 枚 NAND，执行 commit → 等待新区块 → reveal。</p>
              </div>
              <div className="operation-controls">
                <fieldset className="mode-switch">
                  <legend>流片范围</legend>
                  <label>
                    <input
                      type="radio"
                      name="tapeout-mode"
                      value="count"
                      checked={tapeoutMode === "count"}
                      onChange={() => setTapeoutMode("count")}
                      disabled={running}
                    />
                    <span>指定次数</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="tapeout-mode"
                      value="all"
                      checked={tapeoutMode === "all"}
                      onChange={() => setTapeoutMode("all")}
                      disabled={running}
                    />
                    <span>全部流片</span>
                  </label>
                </fieldset>
                {tapeoutMode === "count" ? (
                  <label className="compact-field">
                    <span>每钱包次数</span>
                    <input
                      type="number"
                      min="1"
                      max="39"
                      value={tapeoutCount}
                      onChange={(event) => setTapeoutCount(event.target.value)}
                      disabled={running}
                    />
                  </label>
                ) : (
                  <p className="all-mode-note">
                    使用链上自动识别的全部 NAND，每 5 枚一组；不足 5 枚的余数保留。
                  </p>
                )}
              </div>
              <ActionTooltip
                disabled={tapeoutDisabledReason !== null}
                label={tapeoutDisabledReason}
              >
                <Button
                  disabled={tapeoutDisabledReason !== null}
                  onClick={() => preparePlan("tapeout")}
                >
                  检查并确认流片
                </Button>
              </ActionTooltip>
            </article>
          </div>
        </section>

        <section className="queue-section" aria-labelledby="queue-heading">
          <div className="section-title-row queue-title">
            <div>
              <p className="eyebrow">04 / WALLET LANES</p>
              <h2 id="queue-heading">钱包执行队列</h2>
            </div>
            {progress === null ? null : (
              <div
                className={`batch-progress ${running ? "is-running" : "is-finished"}`}
                role="status"
                aria-label={`${progress.kind === "mint" ? "Mint" : "流片"}进度 ${progress.done}/${progress.total}`}
              >
                <div className="batch-progress-copy">
                  <span>{progress.kind === "mint" ? "Mint" : "流片"}进度</span>
                  <strong className="mono">
                    {progress.done} / {progress.total} ·{" "}
                    {progress.total === 0
                      ? 100
                      : Math.round((progress.done / progress.total) * 100)}
                    %
                  </strong>
                </div>
                <div className="batch-progress-track" aria-hidden="true">
                  <span
                    style={{
                      width: `${progress.total === 0 ? 100 : (progress.done / progress.total) * 100}%`,
                    }}
                  />
                </div>
              </div>
            )}
            <div className="queue-actions">
              {running ? (
                <Button variant="secondary" onClick={stopFutureTasks} disabled={stopping}>
                  {stopping ? "等待当前交易" : "停止后续任务"}
                </Button>
              ) : null}
              <Button
                variant="secondary"
                onClick={exportReport}
                disabled={rows.length === 0}
                title="下载不含私钥的钱包地址、执行状态和交易哈希 JSON"
              >
                导出执行报告
              </Button>
              <Button
                variant="ghost"
                onClick={() => setClearOpen(true)}
                disabled={rows.length === 0 || running}
              >
                清空钱包列表
              </Button>
            </div>
          </div>
          <div className="table-frame">
            <table>
              <thead>
                <tr>
                  <th className="check-cell">
                    <input
                      ref={headerCheckbox}
                      type="checkbox"
                      checked={allSelected}
                      onChange={(event) =>
                        setRows((current) =>
                          current.map((row) => ({ ...row, selected: event.target.checked })),
                        )
                      }
                      aria-label="选择或取消选择全部钱包"
                      disabled={running}
                    />
                  </th>
                  <th>钱包</th>
                  <th>钱包资产</th>
                  <th>NAND ID（流片范围）</th>
                  <th>执行阶段</th>
                  <th>结果</th>
                  <th>
                    <span className="sr-only">操作</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="empty-cell">
                      <WalletCards aria-hidden="true" />
                      <strong>尚未导入钱包</strong>
                      <span>粘贴私钥或选择本地文件后，钱包地址会出现在这里。</span>
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((row, pageIndex) => (
                    <tr key={row.address} data-stage={row.stage}>
                      <td className="check-cell">
                        <input
                          type="checkbox"
                          checked={row.selected}
                          onChange={(event) =>
                            updateRow(row.address, { selected: event.target.checked })
                          }
                          aria-label={`选择钱包 ${shortAddress(row.address)}`}
                          disabled={running}
                        />
                      </td>
                      <td>
                        <div className="wallet-cell">
                          <span className="lane-index mono">
                            {String(walletPage * WALLET_PAGE_SIZE + pageIndex + 1).padStart(2, "0")}
                          </span>
                          <div>
                            <strong className="mono" aria-label={row.address} title={row.address}>
                              {shortAddress(row.address)}
                            </strong>
                            <span>本地签名账户</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="asset-cell">
                          {row.assetError === undefined ? (
                            <>
                              <span>
                                <small>{snapshot?.paymentSymbol ?? "募集资产"}</small>
                                <strong className="mono">
                                  {row.paymentBalance === undefined || snapshot === null
                                    ? "读取中…"
                                    : formatAmount(row.paymentBalance, snapshot.paymentDecimals)}
                                </strong>
                              </span>
                              <span>
                                <small>NAND</small>
                                <strong className="mono">
                                  {row.nandBalance?.toString() ?? "读取中…"}
                                </strong>
                              </span>
                              <span>
                                <small>矿机</small>
                                <strong className="mono">
                                  {row.minerBalance?.toString() ?? "读取中…"}
                                </strong>
                              </span>
                            </>
                          ) : (
                            <span className="asset-error">资产读取失败</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="nand-source-cell">
                          <strong>
                            {snapshot === null
                              ? "待校验项目"
                              : nandScanStatus === "loading"
                                ? "正在同步 NAND ID…"
                                : nandScanStatus === "failed"
                                  ? "NAND ID 同步失败"
                                  : `${row.nandIds.length} 枚可流片 NAND`}
                          </strong>
                          <span
                            className="mono"
                            title={row.nandIds.map(String).join(", ") || "暂无可流片 NAND"}
                          >
                            {row.nandIds.length === 0
                              ? "暂无 ID"
                              : `#${row.nandIds
                                  .slice(0, 10)
                                  .map(String)
                                  .join(", #")}${row.nandIds.length > 10 ? "…" : ""}`}
                          </span>
                          <small>由链上 Transfer 记录自动重建当前所有权</small>
                        </div>
                      </td>
                      <td>
                        <div className={`stage-badge stage-${row.stage}`}>
                          <StageGlyph stage={row.stage} />
                          <span>{stageLabels[row.stage]}</span>
                        </div>
                        <span className="stage-detail">{row.detail}</span>
                      </td>
                      <td>
                        <div className="result-cell">
                          <span className="mono">
                            {row.completed} / {row.total}
                          </span>
                          {row.minerIds.length > 0 ? (
                            <span>矿机 #{row.minerIds.map(String).join(", #")}</span>
                          ) : null}
                          {row.txHash === undefined ? null : (
                            <span className="mono" title={row.txHash}>
                              Tx {shortHash(row.txHash)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <Button
                          variant="ghost"
                          size="compact"
                          onClick={() => removeRow(row.address)}
                          disabled={running}
                        >
                          移除
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {rows.length > WALLET_PAGE_SIZE ? (
            <nav className="queue-pagination" aria-label="钱包列表分页">
              <span>
                第 {walletPage + 1} / {walletPageCount} 页 · 共 {rows.length} 个钱包
              </span>
              <div>
                <Button
                  variant="secondary"
                  size="compact"
                  disabled={walletPage === 0}
                  onClick={() => setWalletPage((current) => Math.max(0, current - 1))}
                >
                  上一页
                </Button>
                <Button
                  variant="secondary"
                  size="compact"
                  disabled={walletPage + 1 >= walletPageCount}
                  onClick={() =>
                    setWalletPage((current) => Math.min(walletPageCount - 1, current + 1))
                  }
                >
                  下一页
                </Button>
              </div>
            </nav>
          ) : null}
          <p className="queue-footnote">
            顺序执行可避免同一钱包 nonce 冲突。“停止后续任务”不会撤销已经广播的交易。
          </p>
        </section>
      </main>

      <ResponsiveDialog
        open={pendingPlan !== null}
        onOpenChange={(open) => {
          if (!open && !running) setPendingPlan(null);
        }}
        title={pendingPlan?.kind === "mint" ? "确认批量 Mint NAND" : "确认批量流片"}
        description="请核对范围和费用。链上交易一经确认不可撤销。"
        closeLabel="关闭确认弹窗"
        dismissible={!running}
        footer={
          <div className="dialog-actions">
            <Button variant="secondary" onClick={() => setPendingPlan(null)} disabled={running}>
              取消
            </Button>
            <Button
              onClick={() => {
                void executePlan();
              }}
              disabled={!acknowledged || running}
            >
              {running ? "执行中" : pendingPlan?.kind === "mint" ? "开始 Mint" : "开始流片"}
            </Button>
          </div>
        }
      >
        {pendingPlan === null || snapshot === null ? null : (
          <div className="confirm-content">
            <dl className="confirm-grid">
              <div>
                <dt>目标项目</dt>
                <dd>
                  {snapshot.tokenName} ·{" "}
                  <span className="mono">{shortAddress(snapshot.address)}</span>
                </dd>
              </div>
              <div>
                <dt>钱包数量</dt>
                <dd className="mono">{pendingPlan.addresses.length}</dd>
              </div>
              <div>
                <dt>预计写交易</dt>
                <dd className="mono">约 {pendingPlan.estimatedWrites} 笔</dd>
              </div>
              {pendingPlan.kind === "mint" ? (
                <div>
                  <dt>Mint 数量模式</dt>
                  <dd>
                    {pendingPlan.quantityMode === "fixed" ? "固定数量" : "随机数量（已锁定）"}
                  </dd>
                </div>
              ) : (
                <div>
                  <dt>流片范围</dt>
                  <dd className="mono">
                    {pendingPlan.tapeoutMode === "all" ? "全部已录入 NAND" : "指定次数"} · 共{" "}
                    {pendingPlan.totalTapeoutCount} 次
                  </dd>
                </div>
              )}
              {pendingPlan.kind === "mint" ? (
                <>
                  <div>
                    <dt>Mint 总量</dt>
                    <dd className="mono">{pendingPlan.totalMintQuantity.toString()} NAND</dd>
                  </div>
                  <div>
                    <dt>购买金额合计</dt>
                    <dd className="mono">
                      {formatAmount(pendingPlan.paymentTotal, snapshot.paymentDecimals)}{" "}
                      {snapshot.paymentSymbol}
                    </dd>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <dt>消耗 NAND</dt>
                    <dd className="mono">{pendingPlan.totalTapeoutCount * 5} NAND</dd>
                  </div>
                  <div>
                    <dt>流片费合计</dt>
                    <dd className="mono">
                      {formatAmount(pendingPlan.nativeFeeTotal, 18)} {FIXED_NATIVE_SYMBOL}
                    </dd>
                  </div>
                </>
              )}
            </dl>
            {pendingPlan.kind === "mint" ? (
              <div className="allocation-preview">
                <strong>每钱包已锁定数量</strong>
                <ul>
                  {pendingPlan.allocations.slice(0, 8).map((allocation) => (
                    <li key={allocation.address}>
                      <span className="mono">{shortAddress(allocation.address)}</span>
                      <span className="mono">
                        {allocation.quantity.toString()} NAND · {allocation.chunks.length} 笔
                      </span>
                    </li>
                  ))}
                </ul>
                {pendingPlan.allocations.length > 8 ? (
                  <p>另有 {pendingPlan.allocations.length - 8} 个钱包</p>
                ) : null}
              </div>
            ) : pendingPlan.skippedAddresses.length > 0 ? (
              <div className="confirm-warning">
                <strong>{pendingPlan.skippedAddresses.length} 个钱包将跳过</strong>
                <p>这些钱包已录入的 NAND 不足 5 枚，不会广播交易。</p>
              </div>
            ) : null}
            <div className="confirm-warning">
              <strong>执行规则</strong>
              <p>
                钱包逐个执行，同一钱包内也按交易顺序执行；失败项会保留在队列中。交易广播后若结果不确定，工具不会自动重试，避免重复扣款。
              </p>
            </div>
            <label className="acknowledge">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(event) => setAcknowledged(event.target.checked)}
              />
              <span>我已核对项目、钱包数量、NAND ID 与费用，并理解链上交易不可撤销。</span>
            </label>
          </div>
        )}
      </ResponsiveDialog>

      <ResponsiveDialog
        open={clearOpen}
        onOpenChange={setClearOpen}
        title="清空钱包列表"
        description="这会移除全部临时签名账户、自动识别的 NAND 和当前执行结果。"
        closeLabel="关闭清空钱包列表弹窗"
        footer={
          <div className="dialog-actions">
            <Button variant="secondary" onClick={() => setClearOpen(false)}>
              保留钱包
            </Button>
            <Button variant="destructive" onClick={clearSession}>
              确认清空
            </Button>
          </div>
        }
      >
        <p>此操作不能撤销。若存在已经 commit 但尚未 reveal 的流片任务，请先完成或记录恢复信息。</p>
      </ResponsiveDialog>
    </div>
  );
}
